const {fetchWithRetry} = require('./Utils/networkRetry');

let nodeFetch;

const initNodeFetch = async () => {
  const module = await import('node-fetch');
  nodeFetch = module.default;
  return nodeFetch;
};

// 不跳轉的 API 路徑 - 這些 API 404 錯誤不會觸發頁面跳轉
const NO_REDIRECT_API_PATHS = [
  '/api/promotion/feedback/',
  '/api/user/profile/',
];

let universalFetch;

async function getUniversalFetch() {
  if (universalFetch) {
    return universalFetch;
  }

  try {
    universalFetch = fetch;
  } catch (ex) {
    if (!nodeFetch) {
      await initNodeFetch();
    }
    universalFetch = nodeFetch;
  }

  return universalFetch;
}

function addTailingSlashForPath(url) {
  let [path, query] = url.split('?');
  if (path[path.length - 1] !== '/') {
    path += '/';
  }
  return `${path}${query ? '?' + query : ''}`;
}

// 檢查是否為不跳轉的 API
const isNoRedirectAPI = (path) => {
  if (!path) return false;
  return NO_REDIRECT_API_PATHS.some((noRedirectPath) =>
    path.includes(noRedirectPath),
  );
};

// 全域錯誤處理函數
// 已移除導頁邏輯（原本會用 window.location 跳轉到 /404 或 /500），改為只 log 不導頁。
// 業務 4xx/5xx 由 caller 自行決定 UX；PostHog SDK 會自動 capture。
// 保留 function signature 與 NO_REDIRECT_API_PATHS 機制，讓既有 callsite 不需全改。
const handleGlobalApiError = (error, status, path) => {
  if (typeof window === 'undefined' || isNoRedirectAPI(path)) {
    return;
  }
  if (process.env.NODE_ENV !== 'production') {
    console.error('[handleGlobalApiError]', {status, path, error});
  }
};

class _ApiProxy {
  constructor({apiUrl}) {
    this._commonHeader = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    this.apiUrl = apiUrl;
    this.apiToken = null;
  }

  _buildEndpoint({path, withHost = false}) {
    if (withHost) {
      return path;
    }

    // `withHost` means we're sending req to our own API server,
    // which is DRF based and we hope to see tailing slash
    //path = addTailingSlashForPath(path);
    return path ? `${this.apiUrl}${path}` : null;
  }

  _buildHeaders({extraHeaders, skipCommonHeader = false, secure = true}) {
    let authHeader =
      this.apiToken && secure ? {Authorization: `Token ${this.apiToken}`} : {};
    if (skipCommonHeader) {
      return authHeader;
    }
    return {
      ...this._commonHeader,
      ...authHeader,
    };
  }

  setToken(token) {
    this.apiToken = token;
  }

  async get({
    path,
    extraHeaders = {},
    withHost = false,
    secure = true,
    skipGlobalErrorHandler = false,
  }) {
    const fetchFunc = await getUniversalFetch();
    return fetchWithRetry(
      fetchFunc,
      this._buildEndpoint({path, withHost}),
      {
        method: 'GET',
        headers: this._buildHeaders({extraHeaders, secure}),
      },
      {retries: 2},
    ).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === '2') {
        return response.json();
      }

      let error = await response.json();

      // 全域錯誤處理（per-request 可用 skipGlobalErrorHandler 關閉）
      if (!skipGlobalErrorHandler) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({status: response.status, ...error});
    });
  }

  async post({
    path,
    extraHeaders = {},
    data,
    withHost = false,
    secure = true,
    idempotent = false,
    skipGlobalErrorHandler = false,
  }) {
    const fetchFunc = await getUniversalFetch();
    return fetchWithRetry(
      fetchFunc,
      this._buildEndpoint({path, withHost}),
      {
        method: 'POST',
        headers: this._buildHeaders({extraHeaders, secure}),
        body: JSON.stringify(data),
      },
      {retries: idempotent ? 1 : 0},
    ).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === '2') {
        return response.json();
      }

      let error = await response.json();
      console.log('error', error);

      // 全域錯誤處理（per-request 可用 skipGlobalErrorHandler 關閉）
      if (!skipGlobalErrorHandler) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({status: response.status, ...error});
    });
  }

  async put({
    path,
    extraHeaders = {},
    data,
    withHost = false,
    secure = true,
    idempotent = false,
    skipGlobalErrorHandler = false,
  }) {
    const fetchFunc = await getUniversalFetch();
    return fetchWithRetry(
      fetchFunc,
      this._buildEndpoint({path, withHost}),
      {
        method: 'PUT',
        headers: this._buildHeaders({extraHeaders, secure}),
        body: JSON.stringify(data),
      },
      {retries: idempotent ? 1 : 0},
    ).then(async (response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === '2') {
        return response.json();
      }

      let error = await response.json();

      // 全域錯誤處理（per-request 可用 skipGlobalErrorHandler 關閉）
      if (!skipGlobalErrorHandler) {
        handleGlobalApiError(error, response.status, path);
      }

      return Promise.reject({status: response.status, ...error});
    });
  }

  async delete({
    path,
    extraHeaders = {},
    withHost = false,
    secure = true,
    idempotent = false,
    skipGlobalErrorHandler = false,
  }) {
    const fetchFunc = await getUniversalFetch();
    return fetchWithRetry(
      fetchFunc,
      this._buildEndpoint({path, withHost}),
      {
        method: 'DELETE',
        headers: this._buildHeaders({extraHeaders, secure}),
      },
      {retries: idempotent ? 1 : 0},
    ).then((response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === '2') {
        return response.json();
      }

      // 全域錯誤處理（per-request 可用 skipGlobalErrorHandler 關閉）
      if (!skipGlobalErrorHandler) {
        handleGlobalApiError(null, response.status, path);
      }

      return Promise.reject({status: response.status});
    });
  }

  async formPost({
    path,
    extraHeaders = {},
    formData,
    withHost = false,
    idempotent = false,
    skipGlobalErrorHandler = false,
  }) {
    const fetchFunc = await getUniversalFetch();
    return fetchWithRetry(
      fetchFunc,
      this._buildEndpoint({path, withHost}),
      {
        method: 'POST',
        headers: this._buildHeaders({extraHeaders, skipCommonHeader: true}),
        body: formData,
      },
      {retries: idempotent ? 1 : 0},
    ).then((response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === '2') {
        return response.json();
      }

      // 全域錯誤處理（per-request 可用 skipGlobalErrorHandler 關閉）
      if (!skipGlobalErrorHandler) {
        handleGlobalApiError(null, response.status, path);
      }

      return Promise.reject({status: response.status});
    });
  }

  async formPut({
    path,
    extraHeaders = {},
    formData,
    withHost = false,
    idempotent = false,
    skipGlobalErrorHandler = false,
  }) {
    const fetchFunc = await getUniversalFetch();
    return fetchWithRetry(
      fetchFunc,
      this._buildEndpoint({path, withHost}),
      {
        method: 'PUT',
        headers: this._buildHeaders({extraHeaders, skipCommonHeader: true}),
        body: formData,
      },
      {retries: idempotent ? 1 : 0},
    ).then((response) => {
      if (response.status === 204) {
        return null;
      }

      if (`${response.status}`[0] === '2') {
        return response.json();
      }

      // 全域錯誤處理（per-request 可用 skipGlobalErrorHandler 關閉）
      if (!skipGlobalErrorHandler) {
        handleGlobalApiError(null, response.status, path);
      }

      return Promise.reject({status: response.status});
    });
  }

  getImage(imageUrl) {
    return this._buildEndpoint({path: imageUrl});
  }
}

module.exports = _ApiProxy;
